import React, { useState, useRef } from 'react';
import { usePortfolioImageUpload } from '../hooks/usePortfolioImageUpload';
import {
  Globe, ExternalLink, UserCircle, Camera, Plus, Image,
  Globe2, AlignLeft, FormInput, Save, Loader2, Check, AlertCircle, RotateCcw, Search
} from 'lucide-react';
import FormFieldList from '../shared/FormFieldList';
import Toggle from '../shared/Toggle';
import { buildSubdomainUrl, buildDisplayDomain } from '../../../utils/domain';

const MobilePortfolioTab = ({ portfolio }) => {
  const { data, docId, setField, setAutomation, setAutomationTags, setCustomFields, updateImageField, isDirty, saving, errors, saveResult, saveChanges, discardChanges, checkDomainAvailability } = portfolio;

  const [domainCheckLoading, setDomainCheckLoading] = useState(false);
  const [domainCheckResult, setDomainCheckResult] = useState(null);

  const customDomain = data?.customDomain?.trim() || '';
  const displayDomain = buildDisplayDomain(customDomain);
  const siteHref = buildSubdomainUrl(customDomain);

  const ownerId = data.ownerId;
  const { uploadAvatar, uploadHeader, uploading } = usePortfolioImageUpload({
    ownerId,
    docId,
    onUrlUpdate: (type, url) => updateImageField(`${type}Image`, url),
  });
  const avatarInputRef = useRef();
  const headerInputRef = useRef();
  const [newFieldValue, setNewFieldValue] = useState('');
  // Add custom field
  const addWebField = () => {
    if (newFieldValue.trim()) {
      const updated = [...(data.customFields || []), newFieldValue.trim()];
      setCustomFields(updated);
      setNewFieldValue('');
    }
  };
  // Remove custom field
  const removeField = (idx) => {
    const updated = (data.customFields || []).filter((_, i) => i !== idx);
    setCustomFields(updated);
  };
  return (
    <div className="space-y-4">
      {/* Live Site Card */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
            <Globe className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-notion-text uppercase tracking-wide">Live Site</h3>
            <a
              href={siteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-stone-600 hover:text-stone-900 underline decoration-stone-300"
            >
              {displayDomain}
            </a>
          </div>
        </div>
        <a
          href={siteHref}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 hover:bg-notion-sidebar rounded-full text-stone-400 hover:text-stone-600"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Global Header Card */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-notion-text flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-stone-400" /> Global Header
          </h3>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center text-stone-400 border border-dashed border-stone-300 relative flex-shrink-0 overflow-hidden group cursor-pointer"
            onClick={() => avatarInputRef.current && avatarInputRef.current.click()}
            style={{ cursor: 'pointer' }}
          >
            {data.avatarImage ? (
              <img src={data.avatarImage} alt="Avatar" className="w-full h-full object-cover rounded-full" />
            ) : (
              <Camera className="w-5 h-5" />
            )}
            <input
              type="file"
              accept="image/*"
              ref={avatarInputRef}
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files && e.target.files[0];
                if (file) uploadAvatar(file);
                e.target.value = '';
              }}
            />
            {uploading.avatar && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs">Uploading...</div>
            )}
            <div className="absolute -bottom-1 -right-1 bg-stone-800 rounded-full p-1 border-2 border-white">
              <Plus className="w-2 h-2 text-white" />
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <div>
              <label className="block text-[10px] font-medium text-stone-500 mb-1">Display Name</label>
              <input
                type="text"
                placeholder="Display Name"
                value={data.displayName || ''}
                onChange={e => setField('displayName', e.target.value)}
                className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-stone-500 mb-1">License #</label>
              <input
                type="text"
                placeholder="License #"
                value={data.license || ''}
                onChange={e => setField('license', e.target.value)}
                className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[10px] font-medium text-stone-500 mb-1">Phone</label>
            <input
              type="text"
              placeholder="Phone"
              value={data.phone || ''}
              onChange={e => setField('phone', e.target.value)}
              className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-stone-500 mb-1">Email</label>
            <input
              type="text"
              placeholder="Email"
              value={data.email || ''}
              onChange={e => setField('email', e.target.value)}
              className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:border-stone-300 border transition"
            />
          </div>
        </div>

        <div className="border-t border-notion-border pt-3">
          <label className="block text-[10px] font-medium text-stone-500 mb-2">Header Background Image</label>
          <button
            className="w-full py-2 bg-stone-50 border border-notion-border rounded text-xs text-stone-600 font-medium flex items-center justify-center gap-2 hover:bg-stone-100 transition"
            type="button"
            onClick={() => headerInputRef.current && headerInputRef.current.click()}
          >
            <Image className="w-3.5 h-3.5" /> Upload Image (1920x400)
          </button>
          <input
            type="file"
            accept="image/*"
            ref={headerInputRef}
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files && e.target.files[0];
              if (file) uploadHeader(file);
              e.target.value = '';
            }}
          />
          {uploading.header && (
            <div className="text-xs text-stone-400 mt-1">Uploading...</div>
          )}
          {data.headerImage && (
            <div className="mt-2 w-full h-20 rounded-lg overflow-hidden border border-stone-200">
              <img src={data.headerImage} alt="Header" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>

      {/* Custom Domain Card */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
        <h3 className="text-sm font-semibold text-notion-text mb-2 flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-stone-400" /> Custom Domain
        </h3>
        <p className="text-[11px] text-stone-500 leading-relaxed mb-3">
          Use your own domain (e.g. sarahjenkins.com). Must be unique across all accounts.
        </p>
        <div className="space-y-2">
          <input
            type="text"
            placeholder="yourdomain.com"
            value={data.customDomain || ''}
            onChange={e => { setField('customDomain', e.target.value); setDomainCheckResult(null); }}
            className={`w-full bg-notion-sidebar border focus:bg-white rounded px-3 py-2 text-xs outline-none transition ${errors.customDomain
              ? 'border-red-300 focus:border-red-400'
              : 'border-transparent focus:border-stone-300'
              }`}
          />
          <button
            type="button"
            onClick={async () => {
              setDomainCheckLoading(true);
              setDomainCheckResult(null);
              try {
                const result = await checkDomainAvailability();
                setDomainCheckResult(result);
              } finally {
                setDomainCheckLoading(false);
              }
            }}
            disabled={domainCheckLoading || !data.customDomain?.trim()}
            className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-[11px] font-medium rounded border border-stone-300 hover:bg-stone-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {domainCheckLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5" />
            )}
            {domainCheckLoading ? 'Checking...' : 'Check Availability'}
          </button>
          {errors.customDomain && (
            <div className="flex items-center gap-1.5 text-red-600 text-[11px]">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{errors.customDomain}</span>
            </div>
          )}
          {!errors.customDomain && domainCheckResult?.available && (
            <div className="flex items-center gap-1.5 text-green-600 text-[11px]">
              <Check className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{domainCheckResult.message}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content & Bio Card */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
        <h3 className="text-sm font-semibold text-notion-text mb-3 flex items-center gap-2">
          <AlignLeft className="w-4 h-4 text-stone-400" /> Content & Bio
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-medium text-stone-500 mb-1">Section Title</label>
            <input
              type="text"
              value={data.aboutTitle || ''}
              onChange={e => setField('aboutTitle', e.target.value)}
              className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none font-medium focus:border-stone-300 border transition"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-stone-500 mb-1">Body Text</label>
            <textarea
              rows="4"
              value={data.aboutBody || ''}
              onChange={e => setField('aboutBody', e.target.value)}
              className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none resize-none focus:border-stone-300 border transition"
            />
          </div>
          <Toggle
            label="Show Active Listings Feed"
            checked={!!data.showActiveListings}
            onChange={val => setField('showActiveListings', val)}
            variant="mobile"
          />
        </div>
      </div>

      {/* Contact Form Card */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
        <h3 className="text-sm font-semibold text-notion-text mb-3 flex items-center gap-2">
          <FormInput className="w-4 h-4 text-stone-400" /> Contact Form
        </h3>

        <FormFieldList
          customFields={data.customFields || []}
          newFieldValue={newFieldValue}
          setNewFieldValue={setNewFieldValue}
          onAddField={addWebField}
          onRemoveField={removeField}
          fieldType="web"
          placeholder="Add field (e.g. Budget)"
          variant="mobile-portfolio"
        />

        <div className="border-t border-notion-border pt-3">
          <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-3">Post-Submission Automations</h4>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!data.automations?.createLead}
                onChange={e => setAutomation('createLead', e.target.checked)}
                className="accent-stone-800 rounded w-3.5 h-3.5"
              />
              <span className="text-xs text-stone-600">Create Lead in CRM</span>
            </label>
          </div>
        </div>
      </div>

      {/* Save Actions */}
      <div className="bg-white border border-notion-border rounded-xl p-4 shadow-card">
        <div className="flex flex-col gap-3">
          {saveResult && (
            <div className={`flex items-center gap-1.5 text-xs ${saveResult.success ? 'text-green-600' : 'text-red-600'}`}>
              {saveResult.success ? (
                <Check className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              <span>{saveResult.success ? 'Changes saved successfully!' : saveResult.error}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            {isDirty && (
              <button
                onClick={discardChanges}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Discard
              </button>
            )}
            <button
              onClick={saveChanges}
              disabled={!isDirty || saving}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDirty ? 'flex-1' : 'w-full'}`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePortfolioTab;
